import MomentUtils from '@date-io/moment';
import { ThemeProvider } from '@material-ui/core';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { enUsTheme } from 'config/theme';
import moment, { Moment } from 'moment';
import React from 'react';
import ReactDOM from 'react-dom';

/**
 * Material date and time picker that can be dropped into the jQuery rendered record
 * forms. The chosen value is mirrored into a hidden input so the existing form
 * serialisation keeps working, in an unambiguous 24 hour format.
 */

export const DATE_TIME_FIELD_VALUE_FORMAT = 'YYYY-MM-DD HH:mm:ss';

const DISPLAY_FORMAT = 'YYYY-MM-DD hh:mm A';

// The app raises Bootstrap's .modal to 10040, well above the Material default of 1300,
// so the picker has to be lifted to stay in front of the record dialog it opens from.
const PICKER_Z_INDEX = 10050;

interface Props {
	name: string;
	label?: string;
	initialValue?: Date | null;
}

function DateTimeField({ name, label, initialValue }: Props) {
	let [value, setValue] = React.useState<Moment | null>(initialValue ? moment(initialValue) : null);

	// An invalid entry is submitted as an empty value rather than a broken string, so the
	// record keeps its previous time instead of being given a meaningless one.
	let serialised = value && value.isValid() ? value.format(DATE_TIME_FIELD_VALUE_FORMAT) : '';

	return (
		// This field is mounted into the jQuery rendered forms through its own React root,
		// which sits outside the app's provider. Without the theme it would fall back to the
		// Material defaults and render smaller text in the wrong colour.
		<ThemeProvider theme={enUsTheme}>
			<MuiPickersUtilsProvider utils={MomentUtils}>
				<KeyboardDateTimePicker
					clearable
					ampm
					showTodayButton
					todayLabel="Today"
					variant="dialog"
					format={DISPLAY_FORMAT}
					placeholder={label}
					invalidDateMessage="Invalid date and time"
					value={value}
					onChange={(date) => setValue(date)}
					fullWidth
					DialogProps={{ style: { zIndex: PICKER_Z_INDEX } }}
				/>
				<input type="hidden" name={name} value={serialised} readOnly />
			</MuiPickersUtilsProvider>
		</ThemeProvider>
	);
}

/**
 * Mounts the field into a plain DOM container and returns a function that unmounts it.
 */
export function mountDateTimeField(container: Element, props: Props) {
	ReactDOM.render(<DateTimeField {...props} />, container as HTMLElement);

	return () => {
		ReactDOM.unmountComponentAtNode(container as HTMLElement);
	};
}
